import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useAuftragCtx } from './AuftragLayout.jsx';
import { useChecklisten, useMe } from '../hooks/data.js';
import { useAbschnitte, useExcelEntwurf } from '../hooks/useExcelEntwurf.js';
import { AbschnittsLeiste, SpeichernLeiste } from '../components/AbschnittsLeiste.jsx';
import { load, save } from '../lib/storage.js';
import { useOnline } from '../hooks/useOnline.js';
import { fileUrl } from '../lib/api.js';
import { isoDate } from '../lib/format.js';
import { Icon } from '../components/Icon.jsx';
import { Stand } from '../components/Stand.jsx';

/** Wie in der Vorlage (api/src/lib/checkliste.js) – Reihenfolge = Excel. */
const ANLAGE = [
  { key: 'hauptzuleitung', titel: 'Hauptzuleitung', uebergaenge: true },
  { key: 'box', titel: 'Box', uebergaenge: true },
  { key: 'verkabelungBox', titel: 'Verkabelung Box', uebergaenge: true },
  { key: 'steuergeraet', titel: 'Steuergerät' },
  { key: 'verkabelungSteuergeraet', titel: 'Verkabelung Steuergerät' },
  { key: 'regensensor', titel: 'Regensensor' },
  { key: 'verkabelungRegensensor', titel: 'Verkabelung Regensensor' },
  { key: 'wasserzaehler', titel: 'Wasserzähler' },
  { key: 'verkabelungWasserzaehler', titel: 'Verkabelung Wasserzähler' },
];
/** Vorschläge für das Bewässerungssystem; dazu kommen die zuletzt verwendeten (auf dem Gerät gemerkt). */
const SYSTEME = ['VAN', 'Rotari', 'Rainbird 3500', 'Tropfrohr'];
const NOTIZEN = ['Aktuell Provisorium', 'Nicht vorhanden'];
const MAX_ZONEN = 40;
/** System der nächsten Zone davor (meist gleich, z. B. «Rotari»). */
const vorigeArt = (zonen, i) => zonen.slice(0, i).reverse().find((z) => z.systemArt)?.systemArt ?? '';
const leereListe = (baustelle) => ({
  version: 1, titel: '', baustelle, zonen: [],
  anlage: Object.fromEntries(ANLAGE.map((t) => [t.key, { erledigt: false, uebergaenge: false, notiz: '', getestet: false, datum: '', zustaendig: '' }])),
});
/** Kurz für die Übersichtszeilen: «30.10.» im laufenden Jahr, sonst «30.10.25». */
const datumKurz = (iso) => {
  if (!iso) return '';
  const [j, m, t] = iso.split('-');
  return j === String(new Date().getFullYear()) ? `${t}.${m}.` : `${t}.${m}.${j.slice(2)}`;
};
const leereZone = (nr) => ({ nr, bezeichnung: '', zuleitung: false, uebergaenge: false, system: false, systemArt: '', getestet: false, datum: '', zustaendig: '' });

export { useOnline };

/**
 * Übersichtsliste Baustelle: Zonen und Anlagenteile abhaken – so wenige Tipps wie möglich.
 * «✓ Alles» erledigt eine Zone mit einem Tipp (inkl. Prüfdatum heute und eigenem Namen). Gespeichert wird als Excel
 * in den Exxas-Dateien des Auftrags; die Datei wird beim nächsten Speichern ersetzt. Eingaben bleiben bis zum
 * Speichern auf dem Gerät (auch bei Funkloch oder Neuladen).
 */
export function Checkliste() {
  const { dateiId } = useParams();
  const { base } = useAuftragCtx();
  if (dateiId !== 'neu' && !/^\d{1,20}$/.test(dateiId ?? '')) return <Navigate to={`${base}/checkliste`} replace />;
  return <ChecklisteBearbeiten key={dateiId} dateiId={dateiId} />; // eigener Zustand pro Liste
}

/** Alle Übersichtslisten des Auftrags (z. B. «Garten Nord», «Dachterrasse») – öffnen oder neu anlegen. */
export function Checklisten() {
  const { nr } = useAuftragCtx();
  return <DokumentListe q={useChecklisten(nr)} pfad="checkliste" entwurfPrefix="checkliste" leer="Für diesen Auftrag gibt es noch keine Übersichtsliste."
    neu="Neue Übersichtsliste" hinweis="Jede Liste ist eine eigene Excel-Datei in den Dateien des Auftrags – z. B. eine pro Gartenteil oder Steuergerät." icon="check" />;
}

/** Liste der Excel-Dokumente eines Auftrags (Übersichtslisten, Bewässerungspläne). */
export function DokumentListe({ q, pfad, entwurfPrefix, leer, neu, hinweis, icon }) {
  const { nr, base } = useAuftragCtx();
  const navigate = useNavigate();
  const listen = q.data?.listen ?? [];
  const neuEntwurf = load(`${entwurfPrefix}.${nr}.neu`, null);
  const offenNeu = !!neuEntwurf;
  const stand = (e) => (e?.vorgemerkt ? 'Wartet auf Netz · ' : e?.fehler ? 'Nicht gespeichert (Konflikt) · ' : e ? 'Nicht gespeicherte Änderungen · ' : '');
  return (
    <div className="narrow">
      <Stand abfragen={[q]} />
      {q.isLoading && <div className="skeleton" style={{ height: 160 }} />}
      {q.isError && !q.data && <div className="empty" role="alert">{q.error.message}<button className="btn" onClick={() => q.refetch()}>Erneut versuchen</button></div>}
      {q.data && (
        <section className="card list" style={{ padding: '4px 16px' }}>
          {listen.length === 0 && <p className="sub" style={{ padding: '12px 0' }}>{leer}</p>}
          {listen.map((l) => (
            <Link key={l.dateiId} to={`${base}/${pfad}/${encodeURIComponent(l.dateiId)}`} className="listrow" style={{ textDecoration: 'none', color: 'var(--ink)' }}>
              <Icon name={icon} size={20} />
              <span className="stack" style={{ gap: 0, flex: 1, minWidth: 0 }}>
                <span className="ellipsis" style={{ fontWeight: 800 }}>{l.titel}</span>
                <span className="sub ellipsis" style={{ fontSize: 12 }}>{stand(load(`${entwurfPrefix}.${nr}.${l.dateiId}`, null))}{l.name}</span>
              </span>
              <Icon name="chevron" size={18} />
            </Link>
          ))}
        </section>
      )}
      <button type="button" className="btn" onClick={() => navigate(`${base}/${pfad}/neu`)} disabled={!q.data && !q.isError && q.fetchStatus !== 'paused'}>
        <Icon name="plus" size={18} stroke={2.4} />{offenNeu ? `${neu} (${neuEntwurf.vorgemerkt ? 'wartet auf Netz' : 'angefangen'})` : neu}
      </button>
      <p className="sub" style={{ fontSize: 12 }}>{hinweis}</p>
    </div>
  );
}

function ChecklisteBearbeiten({ dateiId }) {
  const { nr, base } = useAuftragCtx();
  const me = useMe();
  const online = useOnline();
  const { neu, q, server, daten, entwurf, veraltet, team, aendern, verwerfen, absenden, fehler, speichert, vorgemerkt } = useExcelEntwurf({
    art: 'checklisten', nr, dateiId, base, pfad: 'checkliste', entwurfPrefix: 'checkliste',
    leer: (liste) => leereListe(liste.baustelle),
    titelFehlt: 'Bitte der Liste einen Titel geben (z. B. «Garten Nord»).',
    gespeichert: (d) => `«${d.titel}» gespeichert – Excel liegt in den Dateien`,
  });
  const [arten, setArten] = useState(() => load('checkliste.arten', [])); // zuletzt verwendete, neueste zuerst
  const [anzahl, setAnzahl] = useState('');
  const heute = isoDate();
  // Zuständigkeit: wie zuletzt in dieser Liste eingetragen (z. B. «Flo & Jenny»), sonst der eigene Name
  const zuletztZustaendig = useMemo(() => {
    if (!daten) return '';
    const alle = [...daten.zonen, ...Object.values(daten.anlage)].filter((x) => x.zustaendig && x.datum).sort((a, b) => b.datum.localeCompare(a.datum));
    return alle[0]?.zustaendig ?? '';
  }, [daten]);
  const ichImTeam = team.find((t) => (me.data?.name ?? '').toLowerCase().startsWith(t.toLowerCase())) ?? '';
  const name = zuletztZustaendig || ichImTeam || me.data?.name || '';
  const letzteArt = arten[0] ?? '';
  // Vorschläge: in dieser Liste verwendete, zuletzt verwendete, Standard
  const artVorschlaege = useMemo(() => [...new Set([...(daten?.zonen ?? []).map((z) => z.systemArt).filter(Boolean), ...arten, ...SYSTEME])].slice(0, 8), [daten, arten]);

  // Getestet → Prüfdatum heute und Zuständigkeit; wieder offen → nur das Datum weg (Zuständigkeit bleibt, z. B. «Reserve»)
  const geprueft = (obj, an) => {
    if (an) { if (!obj.datum) obj.datum = heute; if (!obj.zustaendig) obj.zustaendig = name; } else { obj.datum = ''; }
  };

  const setZone = (i, patch) => aendern((d) => {
    const z = { ...d.zonen[i], ...patch };
    if ('getestet' in patch) geprueft(z, patch.getestet);
    d.zonen[i] = z;
    return d;
  });
  const zoneAlles = (i) => aendern((d) => {
    const z = d.zonen[i];
    const fertig = z.zuleitung && z.uebergaenge && z.system && z.getestet;
    Object.assign(z, fertig
      ? { zuleitung: false, uebergaenge: false, system: false, getestet: false }
      : { zuleitung: true, uebergaenge: true, system: true, systemArt: z.systemArt || vorigeArt(d.zonen, i) || letzteArt, getestet: true });
    geprueft(z, !fertig);
    return d;
  });
  const waehleArt = (i, art) => {
    const z = daten.zonen[i];
    if (z.systemArt === art) { setZone(i, { systemArt: '' }); return; }
    setZone(i, { systemArt: art, system: true });
    const neu = [art, ...arten.filter((a) => a !== art)].slice(0, 8);
    setArten(neu);
    save('checkliste.arten', neu);
  };
  const zonenAnlegen = (n) => aendern((d) => {
    const ziel = Math.min(Math.max(n, 1), MAX_ZONEN);
    while (d.zonen.length < ziel) d.zonen.push(leereZone(d.zonen.length + 1));
    return d;
  });
  const zoneWeg = () => aendern((d) => { d.zonen.pop(); return d; });
  const setTeil = (key, patch) => aendern((d) => {
    const t = { ...d.anlage[key], ...patch };
    if ('getestet' in patch) geprueft(t, patch.getestet);
    d.anlage[key] = t;
    return d;
  });
  // «Alle ✓»: alle Teile ohne Notiz (z. B. «Nicht vorhanden») erledigen und testen
  const anlageAlles = () => aendern((d) => {
    const teile = ANLAGE.filter((t) => !d.anlage[t.key].notiz);
    const alle = teile.every((t) => d.anlage[t.key].erledigt && d.anlage[t.key].getestet);
    for (const t of teile) {
      const a = d.anlage[t.key];
      a.erledigt = !alle;
      if (t.uebergaenge) a.uebergaenge = !alle;
      a.getestet = !alle;
      geprueft(a, !alle);
    }
    return d;
  });

  const abschnitte = useAbschnitte(['zonen', 'anlage']);

  const fortschritt = useMemo(() => {
    if (!daten) return null;
    const z = daten.zonen;
    return { getestet: z.filter((x) => x.getestet).length, zonen: z.length, anlage: ANLAGE.filter((t) => daten.anlage[t.key]?.erledigt).length };
  }, [daten]);

  if (q.isLoading && !daten) return <div className="narrow"><div className="skeleton" style={{ height: 120 }} /><div className="skeleton" style={{ height: 320 }} /></div>;
  if (!daten) {
    return (
      <div className="narrow">
        <div className="empty" role="alert">
          {q.error?.message ?? 'Übersichtsliste nicht verfügbar.'}
          <button className="btn" onClick={() => q.refetch()}>Erneut versuchen</button>
        </div>
      </div>
    );
  }

  const datei = server?.datei;
  return (
    <div className="narrow">
      <div className="between" style={{ gap: 8 }}>
        <Link to={`${base}/checkliste`} className="linkbtn" style={{ fontSize: 13, minHeight: 48, display: 'inline-flex', alignItems: 'center' }}>‹ Alle Übersichtslisten</Link>
        <Stand abfragen={[q]} />
      </div>
      <section className="card-dark" style={{ padding: 18, gap: 8 }}>
        <span className="eyebrow on-dark-muted">Übersichtsliste</span>
        <TitelFeld wert={daten.titel ?? ''} onChange={(titel) => aendern((d) => ({ ...d, titel }))} placeholder="Titel, z. B. Garten Nord" max={60} autoFocus={neu} label="Titel der Liste" groesse={24} />
        <span className="on-dark-muted" style={{ fontSize: 13, lineHeight: 1.4 }}>{daten.baustelle || '–'}</span>
        {datei && <ExcelKnopf datei={datei} />}
      </section>

      <AbschnittsLeiste aktiv={abschnitte.aktiv} onSpringe={abschnitte.springe} eintraege={[
        { key: 'zonen', titel: 'Zonen', info: fortschritt.zonen ? `${fortschritt.getestet}/${fortschritt.zonen} getestet` : 'anlegen' },
        { key: 'anlage', titel: 'Anlage', info: `${fortschritt.anlage}/${ANLAGE.length} erledigt` },
      ]} />

      {veraltet && (
        <div className="notice err" role="alert">
          <Icon name="warn" size={18} />
          <span>Die Liste wurde inzwischen geändert (in der App oder in Excel). Deine Eingaben sind noch hier, lassen sich aber nicht speichern.
            <button className="linkbtn" onClick={verwerfen} style={{ marginLeft: 6 }}>Aktuellen Stand laden</button></span>
        </div>
      )}

      <div ref={abschnitte.ref('zonen')} />
      {daten.zonen.length === 0 ? (
        <section className="card">
          <h2 className="h2">Wie viele Zonen hat die Anlage?</h2>
          <div className="wrapchips">
            {[2, 3, 4, 5, 6, 7, 8, 10, 12].map((n) => (
              <button key={n} type="button" className="chip tn" style={{ minWidth: 52 }} onClick={() => zonenAnlegen(n)}>{n}</button>
            ))}
          </div>
          <div className="row" style={{ gap: 8 }}>
            <input className="input" type="number" inputMode="numeric" min={1} max={MAX_ZONEN} value={anzahl} onChange={(e) => setAnzahl(e.target.value)} placeholder="andere Anzahl" style={{ maxWidth: 160 }} />
            <button type="button" className="btn secondary" onClick={() => zonenAnlegen(Number(anzahl) || 1)} disabled={!Number(anzahl)}>Anlegen</button>
          </div>
          <span className="sub" style={{ fontSize: 12 }}>Weitere Zonen lassen sich jederzeit dazunehmen.</span>
        </section>
      ) : (
        <section className="stack" style={{ gap: 10 }}>
          <Abschnittstitel titel="Zonen" info={`${fortschritt.getestet} von ${fortschritt.zonen} getestet`} />
          {daten.zonen.map((z, i) => (
            <Zone key={z.nr} z={z} arten={artVorschlaege} team={team} onChange={(p) => setZone(i, p)} onAlles={() => zoneAlles(i)} onArt={(a) => waehleArt(i, a)} />
          ))}
          <div className="row" style={{ gap: 8 }}>
            <button type="button" className="btn secondary" style={{ flex: 1 }} onClick={() => zonenAnlegen(daten.zonen.length + 1)} disabled={daten.zonen.length >= MAX_ZONEN}>
              <Icon name="plus" size={18} stroke={2.4} />Zone {daten.zonen.length + 1}
            </button>
            <button type="button" className="btn secondary" onClick={zoneWeg} aria-label={`Zone ${daten.zonen.length} entfernen`} title="Letzte Zone entfernen">
              <Icon name="minus" size={18} stroke={2.4} />
            </button>
          </div>
        </section>
      )}

      <div ref={abschnitte.ref('anlage')} style={{ height: 8 }} />
      <Abschnittstitel titel="Anlage" info={`${fortschritt.anlage} von ${ANLAGE.length} erledigt`}
        aktion={<button type="button" className="btn secondary" style={{ minHeight: 48, padding: '0 12px', fontSize: 13 }} onClick={anlageAlles}>Alle ✓</button>} />
      <section className="card" style={{ gap: 0, paddingTop: 4, paddingBottom: 6 }}>
        {ANLAGE.map((t) => (
          <Teil key={t.key} t={t} a={daten.anlage[t.key]} team={team} onChange={(p) => setTeil(t.key, p)} />
        ))}
      </section>

      <SpeichernLeiste entwurf={entwurf} vorgemerkt={vorgemerkt} fehler={fehler} online={online} speichert={speichert} veraltet={veraltet} titelOk={!!daten.titel?.trim()}
        onVerwerfen={verwerfen} onSpeichern={absenden} label="Übersichtsliste speichern" />
    </div>
  );
}

function Haken({ an, label, onClick, breit }) {
  return (
    <button type="button" className={`chip${an ? ' on' : ''}`} aria-pressed={an} onClick={onClick}
      style={{ height: 48, padding: '0 6px', fontSize: 12, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 4, minWidth: breit ? 0 : 92, width: breit ? '100%' : undefined }}>
      {an && !breit && <Icon name="check" size={13} stroke={3} />}{label}
    </button>
  );
}

/** Aufklapp-Zeile: graue Kurzinfo, Tipp zeigt/verbirgt die Details. */
function Kurzinfo({ text, offen, onClick, leer }) {
  return (
    <button type="button" onClick={onClick} aria-expanded={offen}
      style={{ all: 'unset', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: text ? 'var(--ink-2)' : 'var(--muted)', minHeight: 48 }}>
      <span className="ellipsis" style={{ flex: 1, minWidth: 0 }}>{text || leer}</span>
      <span style={{ display: 'inline-flex', transform: `rotate(${offen ? -90 : 90}deg)`, color: 'var(--muted)' }}><Icon name="chevron" size={16} /></span>
    </button>
  );
}

function Zone({ z, arten, team, onChange, onAlles, onArt }) {
  const eigenes = !!z.systemArt && !arten.includes(z.systemArt);
  const [andere, setAndere] = useState(false);
  const [offen, setOffen] = useState(false);
  const kurz = [z.systemArt, datumKurz(z.datum), z.zustaendig].filter(Boolean).join(' · ');
  const fertig = z.zuleitung && z.uebergaenge && z.system && z.getestet;
  return (
    <div className="card" style={{ gap: 10, padding: 14, borderLeft: `4px solid ${fertig ? 'var(--accent)' : 'var(--line-2)'}` }}>
      <div className="row" style={{ gap: 8 }}>
        <span aria-label={`Zone ${z.nr}`} style={{ flexShrink: 0, width: 40, height: 48, borderRadius: 12, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', lineHeight: 1,
          background: fertig ? 'var(--accent)' : 'var(--bg)', color: fertig ? '#fff' : 'var(--ink)' }}>
          <span style={{ fontSize: 9, fontWeight: 700, opacity: 0.7 }}>ZONE</span>
          <span style={{ fontSize: 17, fontWeight: 800 }}>{z.nr}</span>
        </span>
        <input className="input" style={{ height: 48, flex: 1, minWidth: 0 }} value={z.bezeichnung} onChange={(e) => onChange({ bezeichnung: e.target.value })}
          placeholder="Bezeichnung, z. B. Vorgarten" maxLength={80} aria-label={`Bezeichnung Zone ${z.nr}`} />
        <button type="button" className={`btn${fertig ? ' secondary' : ''}`} style={{ minHeight: 48, padding: '0 12px', whiteSpace: 'nowrap' }} onClick={onAlles}
          aria-label={fertig ? `Zone ${z.nr} zurücksetzen` : `Zone ${z.nr} komplett erledigt`}>
          {fertig ? 'Zurück' : '✓ Alles'}
        </button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 6 }}>
        <Haken breit an={z.zuleitung} label="Zuleitung" onClick={() => onChange({ zuleitung: !z.zuleitung })} />
        <Haken breit an={z.uebergaenge} label="Übergänge" onClick={() => onChange({ uebergaenge: !z.uebergaenge })} />
        <Haken breit an={z.system} label="System" onClick={() => onChange({ system: !z.system })} />
        <Haken breit an={z.getestet} label="Getestet" onClick={() => onChange({ getestet: !z.getestet })} />
      </div>
      <Kurzinfo text={kurz} offen={offen} onClick={() => setOffen((x) => !x)} leer="System, Datum, Zuständigkeit" />
      {offen && (<>
      <div className="stack" style={{ gap: 4 }}>
        <span className="sub" style={{ fontSize: 11, fontWeight: 700 }}>Bewässerungssystem</span>
        <div className="wrapchips" aria-label={`Bewässerungssystem Zone ${z.nr}`}>
          {arten.map((a) => (
            <button key={a} type="button" className={`chip accent${z.systemArt === a ? ' on' : ''}`} style={{ height: 48, padding: '0 10px', fontSize: 12 }} onClick={() => onArt(a)}>{a}</button>
          ))}
          <button type="button" className={`chip accent${eigenes || andere ? ' on' : ''}`} style={{ height: 48, padding: '0 10px', fontSize: 12 }} onClick={() => setAndere((x) => !x)}>
            {eigenes ? `✎ ${z.systemArt}` : 'Eigenes …'}
          </button>
        </div>
        {andere && (
          <input className="input" style={{ height: 48 }} autoFocus placeholder="Bewässerungssystem eintippen, z. B. Rainbird 5000" maxLength={60} defaultValue={eigenes ? z.systemArt : ''}
            onBlur={(e) => { const v = e.target.value.trim(); if (v && v !== z.systemArt) onArt(v); setAndere(false); }}
            onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }} />
        )}
      </div>
      <Pruefung obj={z} team={team} onChange={onChange} />
      </>)}
    </div>
  );
}

/** Anlagenteil: eine Zeile mit Erledigt/Getestet und Kurzinfo; Übergänge, Notiz, Datum, Zuständigkeit aufklappbar. */
function Teil({ t, a, team, onChange }) {
  const [offen, setOffen] = useState(false);
  const kurz = [a.uebergaenge && 'Übergänge', a.notiz, datumKurz(a.datum), a.zustaendig].filter(Boolean).join(' · ');
  const mini = (an, label, onClick) => (
    <button type="button" className={`chip${an ? ' on' : ''}`} aria-pressed={an} onClick={onClick}
      style={{ height: 48, padding: 0, width: 72, fontSize: 12, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{label}</button>
  );
  return (
    <div style={{ padding: '10px 0', borderTop: '1px solid var(--line-2)' }}>
      <div className="row" style={{ gap: 6, alignItems: 'center' }}>
        <button type="button" onClick={() => setOffen((x) => !x)} aria-expanded={offen}
          style={{ all: 'unset', cursor: 'pointer', flex: 1, minWidth: 0, minHeight: 48, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 2 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontWeight: 700, fontSize: 14, lineHeight: 1.25 }}>
            {t.titel}
            <span style={{ display: 'inline-flex', transform: `rotate(${offen ? -90 : 90}deg)`, color: 'var(--muted)', flexShrink: 0 }}><Icon name="chevron" size={14} /></span>
          </span>
          {kurz && <span className="ellipsis" style={{ fontSize: 12, color: 'var(--ink-2)' }}>{kurz}</span>}
        </button>
        {mini(a.erledigt, 'Erledigt', () => onChange({ erledigt: !a.erledigt }))}
        {mini(a.getestet, 'Getestet', () => onChange({ getestet: !a.getestet }))}
      </div>
      {offen && (
        <div className="stack" style={{ gap: 10, paddingTop: 10 }}>
          {(t.uebergaenge || a.uebergaenge) && (
            <div style={{ maxWidth: 140 }}><Haken breit an={a.uebergaenge} label="Übergänge" onClick={() => onChange({ uebergaenge: !a.uebergaenge })} /></div>
          )}
          <label className="stack" style={{ gap: 2 }}>
            <span className="sub" style={{ fontSize: 11, fontWeight: 700 }}>Notiz</span>
            <input className="input" style={{ height: 48 }} value={a.notiz} onChange={(e) => onChange({ notiz: e.target.value })} placeholder="z. B. Aktuell Provisorium" maxLength={80} />
          </label>
          <div className="wrapchips" style={{ marginTop: -4 }}>
            {NOTIZEN.map((n) => <button key={n} type="button" className={`chip${a.notiz === n ? ' on' : ''}`} style={{ height: 48, padding: '0 10px', fontSize: 12 }} onClick={() => onChange({ notiz: a.notiz === n ? '' : n })}>{n}</button>)}
          </div>
          <Pruefung obj={a} team={team} onChange={onChange} />
        </div>
      )}
    </div>
  );
}

const TRENNER = /\s*(?:&|\/|,|\+|\bund\b)\s*/i;
const personen = (wert) => String(wert ?? '').split(TRENNER).map((x) => x.trim()).filter(Boolean);

/**
 * Datum der Prüfung (Datumsfeld) und Zuständigkeit (Team-Knöpfe, mehrere möglich → «Flo & Jenny»; «Anderer …» zum
 * Eintippen). Mit «Getestet» füllen sich beide selbst (heute, Zuständigkeit wie zuletzt in der Liste).
 */
function Pruefung({ obj, team, onChange }) {
  return (
    <div className="stack" style={{ gap: 6 }}>
      <label className="stack" style={{ gap: 2, maxWidth: 190 }}>
        <span className="sub" style={{ fontSize: 11, fontWeight: 700 }}>Datum der Prüfung</span>
        <input type="date" className="input" style={{ height: 48, padding: '0 8px', fontSize: 15 }} value={obj.datum} max="2099-12-31"
          onChange={(e) => onChange({ datum: e.target.value })} />
      </label>
      <div className="stack" style={{ gap: 4 }}>
        <span className="sub" style={{ fontSize: 11, fontWeight: 700 }}>Zuständigkeit</span>
        <TeamWahl wert={obj.zustaendig} team={team} onChange={(zustaendig) => onChange({ zustaendig })} />
      </div>
    </div>
  );
}

/** Team-Knöpfe (mehrere möglich → «Flo & Jenny») und «Anderer …» zum Eintippen. */
export function TeamWahl({ wert, team, onChange }) {
  const gewaehlt = personen(wert);
  const eigene = gewaehlt.filter((p) => !team.some((t) => t.toLowerCase() === p.toLowerCase()));
  const [anderer, setAnderer] = useState(false);
  const setzen = (liste) => onChange(liste.join(' & '));
  const umschalten = (t) => {
    const an = gewaehlt.some((p) => p.toLowerCase() === t.toLowerCase());
    setzen(an ? gewaehlt.filter((p) => p.toLowerCase() !== t.toLowerCase()) : [...team.filter((x) => x === t || gewaehlt.some((p) => p.toLowerCase() === x.toLowerCase())), ...eigene]);
  };
  return (
    <>
        <div className="wrapchips">
          {team.map((t) => {
            const an = gewaehlt.some((p) => p.toLowerCase() === t.toLowerCase());
            return <button key={t} type="button" className={`chip${an ? ' on' : ''}`} aria-pressed={an} style={{ height: 48, padding: '0 12px', fontSize: 12 }} onClick={() => umschalten(t)}>{t}</button>;
          })}
          <button type="button" className={`chip${eigene.length || anderer ? ' on' : ''}`} style={{ height: 48, padding: '0 12px', fontSize: 12 }} onClick={() => setAnderer((x) => !x)}>
            {eigene.length ? `✎ ${eigene.join(' & ')}` : 'Anderer …'}
          </button>
        </div>
        {anderer && (
          <input className="input" style={{ height: 48 }} autoFocus placeholder="Name eintippen" maxLength={80} defaultValue={eigene.join(' & ')}
            onBlur={(e) => { setzen([...gewaehlt.filter((p) => !eigene.includes(p)), ...personen(e.target.value)]); setAnderer(false); }}
            onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }} />
        )}
    </>
  );
}

export function Abschnittstitel({ titel, info, aktion }) {
  return (
    <div className="between" style={{ gap: 8, padding: '10px 4px 2px', borderTop: '2px solid var(--line)' }}>
      <div className="stack" style={{ gap: 0 }}>
        <h2 style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.01em' }}>{titel}</h2>
        <span className="sub" style={{ fontSize: 13 }}>{info}</span>
      </div>
      {aktion}
    </div>
  );
}

/** Titel im dunklen Kopf: bricht um (lange Titel bleiben ganz sichtbar), Enter beendet die Eingabe. */
export function TitelFeld({ wert, onChange, placeholder, max, autoFocus, label, groesse = 22 }) {
  const ref = useRef(null);
  useEffect(() => { const el = ref.current; if (el) { el.style.height = 'auto'; el.style.height = `${el.scrollHeight}px`; } }, [wert]);
  return (
    <textarea ref={ref} rows={1} value={wert} onChange={(e) => onChange(e.target.value.replace(/\n/g, ' '))} placeholder={placeholder} maxLength={max} autoFocus={autoFocus} aria-label={label}
      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); } }}
      style={{ all: 'unset', display: 'block', width: '100%', boxSizing: 'border-box', resize: 'none', overflow: 'hidden', overflowWrap: 'anywhere', fontSize: groesse, fontWeight: 800, lineHeight: 1.2, color: '#fff', padding: '2px 0 6px',
        borderBottom: `2px ${wert ? 'solid rgba(255,255,255,0.15)' : 'dashed rgba(255,255,255,0.5)'}` }} />
  );
}

/** «Excel öffnen» als richtiger Knopf im dunklen Kopf. */
export function ExcelKnopf({ datei }) {
  return (
    <a href={fileUrl(datei.id)} download={datei.name}
      style={{ alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 48, padding: '0 14px', borderRadius: 12, background: 'rgba(255,255,255,0.12)', color: '#fff', fontSize: 14, fontWeight: 700, textDecoration: 'none' }}>
      <Icon name="folder" size={16} />Excel öffnen
    </a>
  );
}
